import {LoginAppPageData} from '@/utils/PageData';

export const SETTINGS_ELEMENT_ID = 'login-app-settings';

/**
 * To add a new value to injected JSON configuration, add a key/value to `ExtractedConfig`
 * and a placeholder that will be replaced on the server.
 */

export type ExtractedConfig = {
  recaptchaSiteKey: string;
  segmentWriteKey: string;
  segmentCDNProxy: string;
  pageData: LoginAppPageData;
};

export const PLACEHOLDERS: Record<keyof ExtractedConfig, string> = {
  recaptchaSiteKey: '__RECAPTCHA_SITE_KEY__',
  segmentWriteKey: '__SEGMENT_WRITE_KEY__',
  segmentCDNProxy: '__SEGMENT_CDN_PROXY__',
  pageData: '__PAGE_DATA__',
};

let extractedConfig: ExtractedConfig | undefined;

const extractValue = <T extends keyof ExtractedConfig>(
  parsed: Record<string, any> | undefined,
  jsonKey: T,
): string => {
  const extracted = parsed ? parsed[jsonKey] : null;
  const placeholder = PLACEHOLDERS[jsonKey] || null;
  return extracted && extracted !== placeholder ? extracted : '';
};

export const extractLoginAppSettings = () => {
  if (!extractedConfig) {
    let element: Element | null = null;
    if (typeof document !== 'undefined') {
      element = document.getElementById(SETTINGS_ELEMENT_ID);
    }

    let parsed;

    if (element) {
      try {
        parsed = JSON.parse(element.innerHTML);
      } catch {
        console.error('Invalid login app settings configuration JSON.');
      }
    }

    // Page data is provided as a base64 encoded string to avoid issues with injecting
    // escaped or unescaped JSON strings.
    const pageDataString = extractValue(parsed, 'pageData');
    let pageData: LoginAppPageData = {type: 'placeholder'};
    if (pageDataString) {
      const decoded = atob(pageDataString);
      try {
        pageData = JSON.parse(decoded);
      } catch {
        console.error('Invalid page data configuration in JSON.');
      }
    }

    extractedConfig = {
      recaptchaSiteKey: extractValue(parsed, 'recaptchaSiteKey'),
      segmentWriteKey: extractValue(parsed, 'segmentWriteKey'),
      segmentCDNProxy: extractValue(parsed, 'segmentCDNProxy'),
      pageData,
    };
  }

  return extractedConfig;
};
